// Non-AR fallback: plain three.js orbit viewer of the same anchor content.
// Used on desktop, when camera/motion permission is denied, or via ?mode=viewer.
// Doubles as the desktop verification path for the whole data→CDN→mapping pipeline.
import * as THREE from 'three'
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js'
import type {AnchorGroup} from './anchorGroup'

export interface OrbitViewer {
  stop: () => void
}

export function startOrbitViewer(canvas: HTMLCanvasElement, content: AnchorGroup): OrbitViewer {
  const renderer = new THREE.WebGLRenderer({canvas, antialias: true, alpha: false})
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x11151c)
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 1.2))
  const dir = new THREE.DirectionalLight(0xffffff, 1.4)
  dir.position.set(2, 6, 3)
  scene.add(dir)

  const grid = new THREE.GridHelper(20, 40, 0x2e3947, 0x1d242e)
  scene.add(grid)
  scene.add(content.group)

  // Frame the content: pull the camera back based on bounding sphere.
  const box = new THREE.Box3().setFromObject(content.group)
  const sphere = box.isEmpty() ? null : box.getBoundingSphere(new THREE.Sphere())
  const radius = sphere && sphere.radius > 0.01 ? sphere.radius : 2
  const center = sphere?.center ?? new THREE.Vector3(0, 1, 0)

  const camera = new THREE.PerspectiveCamera(55, 1, 0.01, 1000)
  camera.position.set(center.x + radius * 1.6, center.y + radius * 0.9, center.z + radius * 1.6)

  const controls = new OrbitControls(camera, canvas)
  controls.target.copy(center)
  controls.enableDamping = true
  controls.maxDistance = radius * 12

  const resize = () => {
    const w = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }
  resize()
  window.addEventListener('resize', resize)

  let raf = 0
  let last = performance.now()
  const loop = () => {
    raf = requestAnimationFrame(loop)
    const now = performance.now()
    const dt = Math.min((now - last) / 1000, 0.1)
    last = now
    content.tick(dt, camera.position)
    controls.update()
    renderer.render(scene, camera)
  }
  loop()

  return {
    stop: () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      controls.dispose()
      renderer.dispose()
    },
  }
}
